import { useAppSelector } from '@hooks/app/redux';
import { PlusOutlined } from '@icon';
import { IconButton, PageHeaderWrapper, Table, TableProps, TTableInitialState } from '@ui';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Full } from 'types/common';
import { ITagsPaginationArgs, ModalState, TagItem } from 'types/tags-page';
import { showNotification } from '../../helpers/notification-helper';
import { actions } from '../../store/ducks/tags-page';
import TagDeleteModal from './tag-delete-modal';
import TagDrawer from './tag-drawer/tag-drawer';
import { TableSC } from './tags.styled';
import { useTableColumns } from './tags.util';

type TTableProps = Full<TableProps<TagItem>>;

const INITIAL_PARAMS: ITagsPaginationArgs = {
  page: 1,
  pageSize: 30,
  sortBy: 'name',
  direction: 'ASC',
};

const INITIAL_STATE: TTableInitialState<TagItem> = {
  pageSize: 30,
  pageIndex: 0,
  sortBy: [{ id: 'name', desc: false }],
};

const Tags: React.FC = () => {
  const dispatch = useDispatch();
  const tags = useAppSelector((state) => state.tagsPage.tags);
  const pagination = useAppSelector((state) => state.tagsPage.pagination);
  const errorMessage = useAppSelector((state) => state.tagsPage.errorMessage);
  const successMessage = useAppSelector((state) => state.tagsPage.successMessage);

  const { formatMessage } = useIntl();

  /**
   * initial fetch
   */
  React.useEffect(() => {
    dispatch(actions.getTagsRequest(INITIAL_PARAMS));

    return () => {
      dispatch(actions.reset());
    };
  }, []);

  React.useEffect(() => {
    if (errorMessage) {
      showNotification({
        type: 'ERROR',
        title: formatMessage({
          id: errorMessage,
        }),
        copy: '',
      });
      dispatch(actions.setErrorMessage(''));
    }
  }, [errorMessage]);

  React.useEffect(() => {
    if (successMessage) {
      showNotification({
        type: 'SUCCESS',
        title: formatMessage({
          id: successMessage,
        }),
        copy: '',
      });
      dispatch(actions.setSuccessMessage(''));
      dispatch(actions.getTagsRequest(INITIAL_PARAMS));
    }
  }, [successMessage]);

  const columns = useTableColumns();

  const handleTableChange = React.useCallback<TTableProps['onChange']>((pagination, sorters) => {
    let sortBy: string | undefined;
    let sortDirection: 'ASC' | 'DESC' | undefined;

    if (sorters.length > 0) {
      sortBy = sorters[0].id;
      sortDirection = sorters[0].desc ? 'DESC' : 'ASC';
    }

    dispatch(
      actions.getTagsRequest({
        sortBy: sortBy as any,
        direction: sortDirection as any,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }),
    );
  }, []);

  return (
    <>
      <PageHeaderWrapper>
        <Table.Header
          title={formatMessage({ id: 'tags_table_title' })}
          extra={
            <IconButton
              size="medium"
              Icon={PlusOutlined}
              onClick={() => {
                dispatch(actions.setModalStatus(ModalState.opened));
              }}
            />
          }
        />
        <TableSC
          manualSortBy
          disableSortBy={false}
          disableMultiSort
          columns={columns as any[]}
          data={tags ?? []}
          pagination={pagination}
          onChange={handleTableChange}
          showEmpty
          emptyTitle="Žiadne tagy"
          initialState={INITIAL_STATE}
        />
      </PageHeaderWrapper>
      <TagDrawer />
      <TagDeleteModal />
    </>
  );
};

export default Tags;
